import React, { useEffect, useState } from "react";
import { Button } from "./button";
import { ChevronLeft } from "lucide-react";
import { Link } from "wouter";
import { Capacitor } from "@capacitor/core";

interface MobileHeaderProps {
  title: string;
  backPath?: string;
  rightContent?: React.ReactNode;
  icon?: React.ReactNode;
  transparent?: boolean;
  className?: string;
  subtitle?: React.ReactNode;
}

export function MobileHeader({
  title,
  backPath,
  rightContent,
  icon,
  transparent = false,
  className = "",
  subtitle,
}: MobileHeaderProps) {
  const [safePaddingTop, setSafePaddingTop] = useState(0);
  
  useEffect(() => {
    // When in a Capacitor environment, get the safe area insets
    const getSafeAreaInsets = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          // Set a higher default safe area to avoid status bar overlap
          // iOS status bar is typically 44px (regular) or 47px+ (with notch)
          // Adding extra padding to ensure content is below status bar
          setSafePaddingTop(50);
          
          // Attempt to use status bar information if available
          // Access StatusBar through Capacitor plugins if available
          const capacitorStatusBar = (window as any).Capacitor?.Plugins?.StatusBar;
          if (capacitorStatusBar?.getInfo) {
            try {
              capacitorStatusBar.getInfo().then((info: any) => {
                if (info && info.height) {
                  // Add a small buffer (5px) to ensure we're safely below the status bar
                  setSafePaddingTop(info.height + 5);
                }
              });
            } catch (error) {
              console.log("StatusBar plugin not available", error);
            }
          }
        } catch (error) {
          console.error("Error getting safe area insets:", error);
          // Fallback to a safe value
          setSafePaddingTop(50);
        }
      } else {
        // In web, use minimal padding
        setSafePaddingTop(16);
      }
    };
    
    getSafeAreaInsets();
    
    // Also listen for orientation changes
    const handleResize = () => {
      getSafeAreaInsets();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  return (
    <div 
      className={`sticky top-0 z-30 ${transparent ? 'bg-transparent' : 'bg-black/95 backdrop-blur-lg border-b border-gray-700/50 shadow-md'} ${className} mobile-header transition-all duration-200`}
      style={{ 
        paddingTop: safePaddingTop ? `${safePaddingTop}px` : undefined,
      }}
    >
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="flex items-center">
          {backPath ? (
            <Button
              variant="ghost"
              size="sm"
              asChild
              className="text-gray-300 hover:text-white hover:bg-white/10 rounded-lg mr-3 transition-colors"
            >
              <Link href={backPath}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Link>
            </Button>
          ) : <div className="w-0"></div>}

          <div className="flex flex-col">
            <h1 className="text-lg font-bold text-left text-transparent bg-clip-text bg-gradient-to-r from-white via-primary/80 to-white flex items-center">
              {icon && <span className="mr-2">{icon}</span>}
              {title}
            </h1>
            {subtitle && <div className="text-left text-sm text-gray-300/80">{subtitle}</div>}
          </div>
        </div>

        {rightContent ? (
          <div className="flex-shrink-0">
            {rightContent}
          </div>
        ) : null}
      </div>
    </div>
  );
}